/**
 * Settings → Cleaning & changeover.
 *
 * The plant's line activities — Type A and Type B cleans, swab, changeover,
 * setup, preventive maintenance — and how long each takes on each machine.
 * A machine has one time for every product; a product can have its own time
 * on a machine instead. The line's own crew does the work; a swab also holds
 * the line while the lab tests the sample. Uploaded plans use these times for
 * their cleaning and changeover blocks.
 */
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { Alert, Box, Button, CircularProgress, IconButton, MenuItem, Select, SelectChangeEvent, Switch, Typography } from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";

import { LineActivityKind, LineActivityTime, LineActivityType, SaveLineActivityTime } from "../../../services/line-activities-api";
import { Product } from "../../../services/products-api";
import { planner,PlannerHeader,PlannerPage,SectionLabel } from "../planner-ui"; // eslint-disable-line sort-imports
import { ProductMenuEmpty, ProductMenuSearch, useProductMenuSearch } from "../planner-ui/product-menu-search";
import { useLineActivities } from "./controller/use-line-activities";
import { formatMinutes, parseMinutes, productTimesOn, timeFor, timeKey } from "./utils/line-activities";

interface StoreLocation {
	id: number;
	name: string;
}

interface StoreWorkcenter {
	id: number;
	name: string;
	locationId?: number;
}

const KIND_LABEL: Record<LineActivityKind, string> = {
	cleaning: "Cleaning",
	changeover: "Changeover",
	maintenance: "Maintenance",
	setup: "Setup",
	lab: "Waits for lab",
};
const KINDS = Object.keys(KIND_LABEL) as LineActivityKind[];

const inputSx: React.CSSProperties = {
	font: "inherit",
	fontFamily: planner.fontUi,
	fontSize: 13,
	padding: "5px 8px",
	border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`,
	borderRadius: planner.radiusSm,
	background: planner.surface,
	color: planner.ink,
	minWidth: 0,
	boxSizing: "border-box",
};
const cellSx = { py: 0.75, px: 1.25, borderBottom: `1px solid ${planner.hairline}`, ...planner.type.body, color: planner.ink, verticalAlign: "top" as const };
const headSx = { ...cellSx, ...planner.labelCaps, fontSize: 10, color: planner.inkMuted, bgcolor: planner.surfaceSubtle, borderBottom: `1px solid ${planner.hairlineStrong}`, whiteSpace: "nowrap" as const };

// ---------------------------------------------------------------- a time you type

/** A time field: shows "1 h 30", takes "90", "1.5 h", "1:30"; saves when you leave it. Blank clears it. */
const MinutesInput: React.FC<{ value: number | null | undefined; placeholder?: string; label: string; onSave: (minutes: number | null) => void }> = ({ value, placeholder, label, onSave }) => {
	const [text, setText] = useState(formatMinutes(value));
	const [bad, setBad] = useState(false);
	useEffect(() => {
		setText(formatMinutes(value));
		setBad(false);
	}, [value]);
	const handleChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setText(event.target.value), []);
	const handleBlur = useCallback(() => {
		const minutes = parseMinutes(text);
		if (minutes === undefined) {
			setBad(true);
			return;
		}
		setBad(false);
		if (minutes !== (value ?? null)) onSave(minutes);
		else setText(formatMinutes(value));
	}, [text, value, onSave]);
	const handleKey = useCallback((event: React.KeyboardEvent<HTMLInputElement>) => {
		if (event.key === "Enter") (event.target as HTMLInputElement).blur();
	}, []);
	return (
		<input
			value={text}
			onChange={handleChange}
			onBlur={handleBlur}
			onKeyDown={handleKey}
			placeholder={placeholder}
			aria-label={label}
			title={bad ? "Write a time like 90, 1.5 h or 1:30" : label}
			style={{ ...inputSx, width: 86, fontFamily: planner.fontMono, fontVariantNumeric: "tabular-nums", borderColor: bad ? planner.unstaffed : planner.hairlineStrong }}
		/>
	);
};

// ---------------------------------------------------------------- the activities

const ActivityRow: React.FC<{ type: LineActivityType; onSave: (type: LineActivityType) => void }> = ({ type, onSave }) => {
	const [name, setName] = useState(type.name);
	const [description, setDescription] = useState(type.description ?? "");
	useEffect(() => {
		setName(type.name);
		setDescription(type.description ?? "");
	}, [type]);
	const handleName = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setName(event.target.value), []);
	const handleDescription = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setDescription(event.target.value), []);
	const saveText = useCallback(() => {
		if (name.trim() && (name.trim() !== type.name || description.trim() !== (type.description ?? ""))) onSave({ ...type, name: name.trim(), description: description.trim() });
	}, [name, description, type, onSave]);
	const handleKind = useCallback((event: SelectChangeEvent<string>) => onSave({ ...type, kind: event.target.value as LineActivityKind }), [type, onSave]);
	const handleActive = useCallback((event: React.ChangeEvent<HTMLInputElement>) => onSave({ ...type, isActive: event.target.checked }), [type, onSave]);
	return (
		<Box sx={{ display: "grid", gridTemplateColumns: "190px 150px 1fr 64px", gap: 1.25, alignItems: "center", py: 1, borderBottom: `1px solid ${planner.hairline}`, opacity: type.isActive ? 1 : 0.55 }}>
			<input value={name} onChange={handleName} onBlur={saveText} aria-label="Activity name" style={{ ...inputSx, fontWeight: 600 }} />
			<Select size="small" value={type.kind} onChange={handleKind} aria-label="Kind" sx={{ fontSize: 13, height: 32, bgcolor: planner.surface }}>
				{KINDS.map((kind) => (
					<MenuItem key={kind} value={kind} sx={{ fontSize: 13 }}>
						{KIND_LABEL[kind]}
					</MenuItem>
				))}
			</Select>
			<input value={description} onChange={handleDescription} onBlur={saveText} placeholder="What it means at this plant" aria-label="What it means" style={inputSx} />
			<Switch size="small" checked={type.isActive} onChange={handleActive} inputProps={{ "aria-label": type.isActive ? "In use" : "Not in use" }} />
		</Box>
	);
};

// ---------------------------------------------------------------- a machine

const ProductTimeRow: React.FC<{
	time: LineActivityTime;
	productName: string;
	activityName: string;
	isLab: boolean;
	onSave: (time: SaveLineActivityTime) => void;
}> = ({ time, productName, activityName, isLab, onSave }) => {
	const base = useMemo(() => ({ locationId: time.locationId, activityTypeId: time.activityTypeId, workstation: time.workstation, productId: time.productId }), [time]);
	const saveMinutes = useCallback((minutes: number | null) => onSave({ ...base, minutes, waitMinutes: time.waitMinutes }), [base, time.waitMinutes, onSave]);
	const saveWait = useCallback((wait: number | null) => onSave({ ...base, minutes: time.minutes, waitMinutes: wait ?? 0 }), [base, time.minutes, onSave]);
	const remove = useCallback(() => onSave({ ...base, minutes: null, waitMinutes: 0 }), [base, onSave]);
	return (
		<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 170px 96px 96px 32px", gap: 1, alignItems: "center", py: 0.5 }}>
			<Typography sx={{ ...planner.type.body, color: planner.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={productName}>
				{productName}
			</Typography>
			<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary }}>{activityName}</Typography>
			<MinutesInput value={time.minutes} label={`${activityName} for ${productName}`} onSave={saveMinutes} />
			{isLab ? <MinutesInput value={time.waitMinutes} label="Lab wait" placeholder="lab wait" onSave={saveWait} /> : <span />}
			<IconButton size="small" onClick={remove} aria-label={`Remove ${productName}'s own ${activityName} time`}>
				<CloseIcon fontSize="small" />
			</IconButton>
		</Box>
	);
};

/** Add a product's own time on this machine. */
const AddProductTime: React.FC<{
	products: Product[];
	types: LineActivityType[];
	onAdd: (productId: number, activityTypeId: number, minutes: number) => void;
}> = ({ products, types, onAdd }) => {
	const [productId, setProductId] = useState<number | "">("");
	const [activityTypeId, setActivityTypeId] = useState<number | "">(types[0]?.id ?? "");
	const [text, setText] = useState("");
	const search = useProductMenuSearch(products, productId);
	const minutes = parseMinutes(text);
	const ready = productId !== "" && activityTypeId !== "" && typeof minutes === "number";
	const handleProduct = useCallback((event: SelectChangeEvent<number | "">) => setProductId(event.target.value === "" ? "" : Number(event.target.value)), []);
	const handleActivity = useCallback((event: SelectChangeEvent<number | "">) => setActivityTypeId(event.target.value === "" ? "" : Number(event.target.value)), []);
	const handleText = useCallback((event: React.ChangeEvent<HTMLInputElement>) => setText(event.target.value), []);
	const add = useCallback(() => {
		if (productId !== "" && activityTypeId !== "" && typeof minutes === "number") {
			onAdd(productId, activityTypeId, minutes);
			setProductId("");
			setText("");
		}
	}, [productId, activityTypeId, minutes, onAdd]);
	return (
		<Box sx={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) 170px 96px auto", gap: 1, alignItems: "center", pt: 1 }}>
			<Select size="small" displayEmpty value={productId} onChange={handleProduct} {...search.selectProps} sx={{ fontSize: 13, height: 32, bgcolor: planner.surface }}>
				<ProductMenuSearch query={search.query} onQueryChange={search.setQuery} />
				<MenuItem value="" sx={{ fontSize: 13, color: planner.inkMuted }}>
					Pick a product
				</MenuItem>
				{search.visible.map((product) => (
					<MenuItem key={product.id} value={product.id} sx={{ fontSize: 13 }}>
						{product.name}
					</MenuItem>
				))}
				{search.isEmpty && <ProductMenuEmpty />}
			</Select>
			<Select size="small" value={activityTypeId} onChange={handleActivity} sx={{ fontSize: 13, height: 32, bgcolor: planner.surface }}>
				{types.map((type) => (
					<MenuItem key={type.id} value={type.id} sx={{ fontSize: 13 }}>
						{type.name}
					</MenuItem>
				))}
			</Select>
			<input value={text} onChange={handleText} placeholder="e.g. 5 h" aria-label="Time" style={{ ...inputSx, width: 96, fontFamily: planner.fontMono }} />
			<Button size="small" startIcon={<AddIcon />} disabled={!ready} onClick={add} sx={{ textTransform: "none", color: planner.navy, justifySelf: "start" }}>
				Add
			</Button>
		</Box>
	);
};

const MachineRows: React.FC<{
	machine: string;
	locationId: number;
	types: LineActivityType[];
	times: LineActivityTime[];
	index: Map<string, LineActivityTime>;
	products: Product[];
	productNames: Map<number, string>;
	onSave: (time: SaveLineActivityTime) => void;
}> = ({ machine, locationId, types, times, index, products, productNames, onSave }) => {
	const [open, setOpen] = useState(false);
	const own = useMemo(() => productTimesOn(times, machine).filter((t) => types.some((type) => type.id === t.activityTypeId)), [times, machine, types]);
	const toggle = useCallback(() => setOpen((value) => !value), []);
	const addProductTime = useCallback(
		(productId: number, activityTypeId: number, minutes: number) => {
			const machineTime = timeFor(index, activityTypeId, machine);
			onSave({ locationId, activityTypeId, workstation: machine, productId, minutes, waitMinutes: machineTime?.waitMinutes ?? 0 });
		},
		[index, machine, locationId, onSave]
	);
	return (
		<>
			<tr>
				<Box component="td" sx={{ ...cellSx, position: "sticky", left: 0, bgcolor: planner.surface, zIndex: 1, minWidth: 190 }}>
					<Typography sx={{ ...planner.type.bodyStrong, color: planner.navy }}>{machine}</Typography>
					<Button size="small" onClick={toggle} endIcon={open ? <ExpandLessIcon /> : <ExpandMoreIcon />} sx={{ textTransform: "none", p: 0, minWidth: 0, fontSize: 12, color: own.length ? planner.accent : planner.inkMuted }}>
						{own.length ? `${own.length} product time${own.length === 1 ? "" : "s"}` : "Times per product"}
					</Button>
				</Box>
				{types.map((type) => (
					<MachineCell key={type.id} type={type} machine={machine} locationId={locationId} time={index.get(timeKey(type.id, machine, null))} onSave={onSave} />
				))}
			</tr>
			{open && (
				<tr>
					<Box component="td" colSpan={types.length + 1} sx={{ ...cellSx, bgcolor: planner.alloyWarm, px: 3, py: 1.5 }}>
						<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary, mb: 0.5 }}>
							A product&apos;s own time on {machine} replaces the machine&apos;s time above for that product.
						</Typography>
						{own.map((time) => {
							const type = types.find((t) => t.id === time.activityTypeId);
							return (
								<ProductTimeRow
									key={time.id}
									time={time}
									productName={productNames.get(time.productId ?? 0) ?? `Product ${time.productId}`}
									activityName={type?.name ?? ""}
									isLab={type?.kind === "lab"}
									onSave={onSave}
								/>
							);
						})}
						<AddProductTime products={products} types={types} onAdd={addProductTime} />
					</Box>
				</tr>
			)}
		</>
	);
};

const MachineCell: React.FC<{ type: LineActivityType; machine: string; locationId: number; time?: LineActivityTime; onSave: (time: SaveLineActivityTime) => void }> = ({ type, machine, locationId, time, onSave }) => {
	const saveMinutes = useCallback(
		(minutes: number | null) => onSave({ locationId, activityTypeId: type.id, workstation: machine, productId: null, minutes, waitMinutes: time?.waitMinutes ?? 0 }),
		[locationId, type.id, machine, time, onSave]
	);
	const saveWait = useCallback(
		(wait: number | null) => onSave({ locationId, activityTypeId: type.id, workstation: machine, productId: null, minutes: time?.minutes ?? 0, waitMinutes: wait ?? 0 }),
		[locationId, type.id, machine, time, onSave]
	);
	return (
		<Box component="td" sx={cellSx}>
			<Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
				<MinutesInput value={time?.minutes} placeholder="—" label={`${type.name} on ${machine}`} onSave={saveMinutes} />
				{type.kind === "lab" && <MinutesInput value={time?.waitMinutes} placeholder="lab wait" label={`Lab wait after ${type.name} on ${machine}`} onSave={saveWait} />}
			</Box>
		</Box>
	);
};

// ---------------------------------------------------------------- page

const LineActivities: React.FC = () => {
	const locationId = useSelector((state: { fullTimeShifts?: { selectedLocation?: number } }) => Number(state.fullTimeShifts?.selectedLocation || 0));
	const locations = useSelector((state: { fullTimeShifts?: { filterData?: { allLocations?: StoreLocation[] } } }) => state.fullTimeShifts?.filterData?.allLocations ?? []);
	const allWorkcenters = useSelector((state: { fullTimeShifts?: { filterData?: { allWorkcenters?: StoreWorkcenter[] } } }) => state.fullTimeShifts?.filterData?.allWorkcenters ?? []);
	const plantName = locations.find((l) => l.id === locationId)?.name ?? "";
	const machines = useMemo(
		() => Array.from(new Set(allWorkcenters.filter((wc) => !locationId || wc.locationId === locationId).map((wc) => String(wc.name)))).sort((a, b) => a.localeCompare(b)),
		[allWorkcenters, locationId]
	);

	const { types, activeTypes, times, index, products, productNames, loading, error, saveType, addType, saveTime, dismissError } = useLineActivities(locationId);

	return (
		<PlannerPage>
			<PlannerHeader
				title="Cleaning & changeover"
				subtitle={`${plantName ? `${plantName} · ` : ""}How long each cleaning, changeover and check takes on each machine. The line's own crew does them.`}
			/>
			{error && (
				<Alert severity="warning" onClose={dismissError} sx={{ mb: 2 }}>
					{error}
				</Alert>
			)}
			{loading && <CircularProgress size={22} sx={{ color: planner.accent }} />}
			{!loading && (
				<>
					<Box sx={{ bgcolor: planner.surface, border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`, borderRadius: `${planner.radius}px`, px: 2, py: 1.5, mb: 3 }}>
						<Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
							<SectionLabel>Activities</SectionLabel>
							<Button size="small" startIcon={<AddIcon />} onClick={addType} sx={{ textTransform: "none", color: planner.navy }}>
								Add activity
							</Button>
						</Box>
						{types.map((type) => (
							<ActivityRow key={type.id} type={type} onSave={saveType} />
						))}
					</Box>

					<SectionLabel sx={{ mb: 1 }}>Times by machine</SectionLabel>
					<Typography sx={{ ...planner.type.meta, color: planner.inkSecondary, mb: 1 }}>
						One time per machine applies to every product. Write 90, 1.5 h or 1:30; leave blank for none. A swab&apos;s second field is how long the line waits for the lab.
					</Typography>
					{machines.length === 0 ? (
						<Typography sx={{ ...planner.type.body, color: planner.inkMuted }}>This plant has no machines yet.</Typography>
					) : (
						<Box sx={{ overflow: "auto", bgcolor: planner.surface, border: `${planner.borderWidth}px solid ${planner.hairlineStrong}`, borderRadius: `${planner.radius}px` }}>
							<Box component="table" sx={{ borderCollapse: "collapse", width: "100%" }}>
								<thead>
									<tr>
										<Box component="th" sx={{ ...headSx, textAlign: "left", position: "sticky", left: 0, zIndex: 2 }}>
											Machine
										</Box>
										{activeTypes.map((type) => (
											<Box component="th" key={type.id} sx={{ ...headSx, textAlign: "left" }} title={type.description ?? undefined}>
												{type.name}
											</Box>
										))}
									</tr>
								</thead>
								<tbody>
									{machines.map((machine) => (
										<MachineRows
											key={machine}
											machine={machine}
											locationId={locationId}
											types={activeTypes}
											times={times}
											index={index}
											products={products}
											productNames={productNames}
											onSave={saveTime}
										/>
									))}
								</tbody>
							</Box>
						</Box>
					)}
				</>
			)}
		</PlannerPage>
	);
};

export default LineActivities;
